// src/ui/StartChatModal.jsx — find someone and start talking to them.
//
// The search is the directory search the rest of the web already uses, which
// runs FOUR prefix queries in parallel — name, username, phone (raw and
// digits-only) and email — so any of those four gets you there.
//
// Note to Self is pinned above the results and always shown, exactly as the
// app's people search does (HomeScreen pins it as a fixed first row rather
// than something you can search your way past), carrying the same verified
// mark it wears in the chat list.

import React, { useEffect, useState } from 'react';
import Avatar, { resolveName } from './Avatar';
import { X, Search, Verified, DocText } from './icons';
import { searchUsers } from '../services/users';

export default function StartChatModal({ myUid, onPick, onPickSelf, onClose }) {
  const [term, setTerm] = useState('');
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!term.trim()) { setResults([]); return undefined; }
    let live = true;
    setBusy(true);
    const t = setTimeout(() => {
      searchUsers(term, myUid)
        .then((r) => { if (live) { setResults(r); setBusy(false); } })
        .catch(() => live && setBusy(false));
    }, 250);
    return () => { live = false; clearTimeout(t); };
  }, [term, myUid]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="ng-modal" onClick={onClose}>
      <div className="ng-card sc-card" onClick={(e) => e.stopPropagation()}>
        <div className="ng-head">
          <b><Search width={16} height={16} /> New chat</b>
          <button className="icon-btn" onClick={onClose} aria-label="Close"><X /></button>
        </div>

        <div className="ng-body">
          <div className="ng-search">
            <Search width={16} height={16} />
            <input
              autoFocus value={term}
              placeholder="Name, username, phone or email"
              onChange={(e) => setTerm(e.target.value)}
            />
          </div>

          {/* 🟢 always there, never filtered away */}
          <button className="ng-row sc-self" onClick={() => { onPickSelf(); onClose(); }}>
            {/* 🟥 the NOTE mark, not my photo. Passing my profile here made the row
                resolve to my own picture a beat after mount — which reads as "me",
                not as the notebook. The app uses a fixed glyph; so do we. */}
            <span className="sc-note"><DocText width={18} height={18} /></span>
            <span className="ng-row-who">
              <b>Note to Self <Verified width={14} height={14} /></b>
              <i>Message yourself — notes, links, files</i>
            </span>
          </button>

          <div className="ng-results">
            {busy && <div className="ci-empty">Searching…</div>}
            {!busy && term.trim() && results.length === 0 && (
              <div className="ci-empty">Nobody found by that name, username, phone or email</div>
            )}
            {results.map((u) => (
              <button className="ng-row" key={u.uid} onClick={() => { onPick(u); onClose(); }}>
                <Avatar profile={u} size="sm" myUid={myUid} />
                <span className="ng-row-who">
                  <b>{resolveName(u)}</b>
                  <i>{u.username ? `@${u.username}` : (u.phoneE164 || u.phone || u.email || '')}</i>
                </span>
              </button>
            ))}
            {!term.trim() && (
              <div className="sc-hint">Search by name, @username, phone number or email.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
